/**
 * Isolated parser for TIDY "Income Estimate" PDFs.
 *
 * Self-contained and framework-free (no Angular, no app imports) so it stays
 * easy to test and swap out. It does NOT know anything about the P&L grid or
 * backend line-item keys — it only reads the labeled annual figures out of the
 * PDF. Mapping those onto backend keys + monthly distribution lives in
 * `income-estimate-projection-rules.ts`.
 *
 * The TIDY PDF uses subset CID fonts with per-glyph positioning, so the text is
 * not directly readable. We:
 *   1. inflate every Flate-compressed stream,
 *   2. build a ToUnicode map from the font CMaps,
 *   3. walk the content-stream text operators, decoding each glyph and breaking
 *      lines on vertical text-position changes,
 *   4. read the labeled dollar amounts off the resulting line-oriented text.
 */

export interface ParsedIncomeEstimateExpense {
  /** Stable internal id for this PDF line (see EXPENSE_LABELS). */
  id: string;
  /** Human label exactly as printed in the PDF. */
  label: string;
  /** Signed integer cents — always negative (an expense). */
  annualCents: number;
}

export interface ParsedIncomeEstimate {
  /** Annual gross revenue in positive integer cents. */
  grossRevenueAnnualCents: number;
  /** Annual expense lines in negative integer cents. */
  expenses: ParsedIncomeEstimateExpense[];
  /** Net operating income (annual) in cents — for display / sanity only. */
  noiAnnualCents?: number;
}

/** Thrown when the PDF can't be read as a TIDY income estimate. */
export class IncomeEstimateParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IncomeEstimateParseError';
  }
}

// -- PDF format knowledge (which labels to read) ------------------------------

const GROSS_LABEL = 'Annual gross revenue';
const NOI_LABEL = 'Net (annual)';

/**
 * Short/mid-term (STR) expense lines we read out of the estimate. Labels are
 * matched against a single extracted line, which lets us read the broken-out
 * detail values ("Insurance" → −$1,500) without matching the combined page-1
 * line ("Insurance, maintenance"). Some lines only appear in the richer
 * LTR-vs-STR comparison layout (furnishing depreciation, permit + TOT); they're
 * simply absent in the single-column STR estimate.
 */
const EXPENSE_LABELS: { id: string; label: string }[] = [
  { id: 'airbnb_host_fee',         label: 'Airbnb host fee' },
  { id: 'tidy_fee',                label: 'TIDY fee' },
  { id: 'utilities',               label: 'Utilities (host pays)' },
  { id: 'consumables',             label: 'Consumables / supplies' },
  { id: 'furnishing_depreciation', label: 'Furnishing depreciation' },
  { id: 'permit_tot',              label: 'Permit + TOT' },
  { id: 'insurance',               label: 'Insurance' },
  { id: 'maintenance_reserve',     label: 'Maintenance reserve' },
];

/** One column on a row: positive cents, or `null` for an empty "—" column. */
type RowCell = number | null;

// -- Pure text parsing (synchronous, unit-testable) ---------------------------

/**
 * Read a {@link ParsedIncomeEstimate} from already-extracted, line-oriented PDF
 * text. Handles both the single-column short/mid-term estimate and the richer
 * LTR-vs-STR comparison layout — in the latter, the short/mid-term figures are
 * the SECOND column, so we read that one. Pure and synchronous; exported
 * separately so it's testable with a text fixture (no Web decompression API).
 */
export function extractIncomeEstimate(text: string): ParsedIncomeEstimate {
  const lines = text.split('\n').map((l) => l.trim());

  /**
   * The value cells (columns) on the row that begins at `label`: the
   * consecutive following lines that contain no letters. Each column is a cell;
   * a "—" is an empty cell. Handles a "$" rendered apart from its digits
   * (e.g. "Net (annual)" → "$" → "47,027"). Returns null if the label is absent.
   */
  const rowCells = (label: string): RowCell[] | null => {
    const i = lines.indexOf(label);
    if (i === -1) {
      return null;
    }
    const tokens: string[] = [];
    for (let k = i + 1; k < lines.length; k++) {
      const ln = lines[k];
      if (ln === '') { continue; }
      if (/[A-Za-z]/.test(ln)) { break; } // a line with letters is the next label
      tokens.push(...ln.split(/\s+/).filter(Boolean));
      if (tokens.length > 16) { break; }
    }
    const cells: RowCell[] = [];
    for (let t = 0; t < tokens.length; t++) {
      let tok = tokens[t];
      if (tok === '$') { tok += tokens[++t] ?? ''; } // merge a lone "$" with its digits
      if (!/\d/.test(tok)) {
        // No digits: an em/en-dash (or minus) is an empty column; ignore "%", "$".
        if (/[‒–—−-]/.test(tok)) { cells.push(null); }
        continue;
      }
      const digits = tok.replace(/[^\d.]/g, '');
      const [whole, frac] = digits.split('.');
      cells.push(parseInt(whole || '0', 10) * 100 + (frac ? parseInt(frac.padEnd(2, '0').slice(0, 2), 10) : 0));
    }
    return cells;
  };

  // Single vs dual column (LTR-vs-STR comparison): any row with 2+ cells → dual,
  // and the short/mid-term value is the second column.
  const sampledRows = [GROSS_LABEL, NOI_LABEL, ...EXPENSE_LABELS.map((e) => e.label)]
    .map((l) => rowCells(l))
    .filter((c): c is RowCell[] => c !== null);
  const strIndex = sampledRows.some((c) => c.length >= 2) ? 1 : 0;

  /** Short/mid-term value (positive cents) for a label, or null if absent / "—". */
  const strCents = (label: string): number | null => rowCells(label)?.[strIndex] ?? null;

  const gross = strCents(GROSS_LABEL);
  if (gross === null) {
    throw new IncomeEstimateParseError(
      'Could not find an annual gross revenue figure — is this a TIDY income estimate PDF?',
    );
  }

  const expenses: ParsedIncomeEstimateExpense[] = [];
  for (const def of EXPENSE_LABELS) {
    const cents = strCents(def.label);
    if (cents !== null) {
      expenses.push({ id: def.id, label: def.label, annualCents: -Math.abs(cents) });
    }
  }

  const noi = strCents(NOI_LABEL);

  return {
    grossRevenueAnnualCents: gross,
    expenses,
    noiAnnualCents: noi ?? undefined,
  };
}

// -- PDF decoding -------------------------------------------------------------

/**
 * Treat each byte as a code unit (Latin-1) so byte offsets line up with chars.
 * Done by hand rather than via `TextDecoder('latin1')` — that label maps to
 * windows-1252 and would mangle bytes 0x80–0x9F, corrupting the deflate input.
 */
function bytesToLatin1(bytes: Uint8Array): string {
  let out = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    out += String.fromCharCode(...Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return out;
}

function latin1ToBytes(str: string): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    out[i] = str.charCodeAt(i) & 0xff;
  }
  return out;
}

/** Inflate a zlib/deflate buffer using the Web `DecompressionStream` API. */
async function inflate(bytes: Uint8Array<ArrayBuffer>, format: 'deflate' | 'deflate-raw'): Promise<Uint8Array> {
  const ds = new DecompressionStream(format);
  const writer = ds.writable.getWriter();
  void writer.write(bytes);
  void writer.close();
  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    chunks.push(value);
    total += value.length;
  }
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.length;
  }
  return out;
}

/** Inflate every Flate stream in the PDF, returned as Latin-1 strings. */
async function inflateStreams(pdf: string): Promise<string[]> {
  const out: string[] = [];
  let idx = 0;
  for (;;) {
    const s = pdf.indexOf('stream', idx);
    if (s === -1) {
      break;
    }
    const e = pdf.indexOf('endstream', s);
    if (e === -1) {
      break;
    }
    let start = s + 'stream'.length;
    if (pdf.charCodeAt(start) === 0x0d) { start++; }
    if (pdf.charCodeAt(start) === 0x0a) { start++; }
    let end = e;
    if (pdf.charCodeAt(end - 1) === 0x0a) { end--; }
    if (pdf.charCodeAt(end - 1) === 0x0d) { end--; }
    idx = e + 'endstream'.length;

    // Only bother with zlib-headered streams (0x78 ...); skip images/binary.
    if (pdf.charCodeAt(start) !== 0x78) {
      continue;
    }
    const chunk = latin1ToBytes(pdf.slice(start, end));
    try {
      out.push(bytesToLatin1(await inflate(chunk, 'deflate')));
    } catch {
      try {
        out.push(bytesToLatin1(await inflate(chunk, 'deflate-raw')));
      } catch {
        // Not a decodable stream — ignore.
      }
    }
  }
  return out;
}

/** Build a code→unicode map by merging all ToUnicode CMaps in the document. */
function buildToUnicode(streams: string[]): Record<number, string> {
  const map: Record<number, string> = {};
  const hexToStr = (h: string): string => {
    let s = '';
    for (let i = 0; i < h.length; i += 4) {
      s += String.fromCharCode(parseInt(h.slice(i, i + 4), 16));
    }
    return s;
  };
  for (const cm of streams) {
    if (!/beginbfchar|beginbfrange/.test(cm)) {
      continue;
    }
    let block: RegExpExecArray | null;
    const bfchar = /beginbfchar([\s\S]*?)endbfchar/g;
    while ((block = bfchar.exec(cm))) {
      const pair = /<([0-9A-Fa-f]+)>\s*<([0-9A-Fa-f]+)>/g;
      let m: RegExpExecArray | null;
      while ((m = pair.exec(block[1]))) {
        map[parseInt(m[1], 16)] = hexToStr(m[2]);
      }
    }
    const bfrange = /beginbfrange([\s\S]*?)endbfrange/g;
    while ((block = bfrange.exec(cm))) {
      const triple = /<([0-9A-Fa-f]+)>\s*<([0-9A-Fa-f]+)>\s*<([0-9A-Fa-f]+)>/g;
      let m: RegExpExecArray | null;
      while ((m = triple.exec(block[1]))) {
        const lo = parseInt(m[1], 16);
        const hi = parseInt(m[2], 16);
        const base = parseInt(m[3], 16);
        for (let c = lo; c <= hi; c++) {
          map[c] = String.fromCharCode(base + (c - lo));
        }
      }
    }
  }
  return map;
}

/**
 * Walk the content streams' text-showing operators, decoding glyphs via the
 * ToUnicode map and emitting a newline whenever the text Y-position changes
 * (so glyphs that share a baseline stay on one line). Word spacing comes from
 * the PDF's own space glyph (unicode 0x20).
 */
function extractTextFromStreams(streams: string[], uni: Record<number, string>): string {
  const decodeHex = (hex: string): string => {
    let s = '';
    for (let i = 0; i < hex.length; i += 2) {
      const code = parseInt(hex.slice(i, i + 2), 16);
      const mapped = uni[code];
      if (mapped !== undefined) {
        s += mapped;
      }
    }
    return s;
  };

  const outLines: string[] = [];
  const token =
    /(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+(-?[\d.]+)\s+Tm|(-?[\d.]+)\s+(-?[\d.]+)\s+(?:Td|TD)|(T\*)|<([0-9A-Fa-f]+)>\s*Tj|\[((?:[^\]\\]|\\.)*)\]\s*TJ/g;

  for (const stream of streams) {
    if (stream.indexOf('BT') === -1) {
      continue;
    }
    const bt = /BT([\s\S]*?)ET/g;
    let block: RegExpExecArray | null;
    while ((block = bt.exec(stream))) {
      let lastY: number | null = null;
      let line = '';
      const flush = (): void => {
        if (line.length) {
          outLines.push(line);
        }
        line = '';
      };
      token.lastIndex = 0;
      let t: RegExpExecArray | null;
      while ((t = token.exec(block[1]))) {
        if (t[1] !== undefined) {
          // Tm — absolute text matrix; operand f (index 6) is the Y translate.
          const y = parseFloat(t[6]);
          if (lastY !== null && Math.abs(y - lastY) > 0.5) {
            flush();
          }
          lastY = y;
        } else if (t[7] !== undefined) {
          // Td / TD — relative move; ty (index 8) non-zero means a new line.
          const ty = parseFloat(t[8]);
          if (Math.abs(ty) > 0.5) {
            flush();
            if (lastY !== null) {
              lastY += ty;
            }
          }
        } else if (t[9] === 'T*') {
          flush();
        } else if (t[10] !== undefined) {
          line += decodeHex(t[10]);
        } else if (t[11] !== undefined) {
          const part = /<([0-9A-Fa-f]+)>|(?:-?[\d.]+)/g;
          let p: RegExpExecArray | null;
          while ((p = part.exec(t[11]))) {
            if (p[1] !== undefined) {
              line += decodeHex(p[1]);
            }
          }
        }
      }
      flush();
    }
  }
  return outLines.join('\n');
}

/** Extract the full line-oriented text content from PDF bytes. */
export async function extractPdfText(bytes: Uint8Array): Promise<string> {
  const pdf = bytesToLatin1(bytes);
  const streams = await inflateStreams(pdf);
  const uni = buildToUnicode(streams);
  return extractTextFromStreams(streams, uni);
}

/**
 * Whether this browser can parse the PDF. We inflate via the Web
 * `DecompressionStream` API (Compression Streams), which is unavailable before
 * Safari/iOS 16.4 even though the app's browserslist allows iOS 16.0+. Callers
 * should gate the import entry point on this so users on older WebViews aren't
 * offered a flow that can only fail.
 */
export function isPdfImportSupported(): boolean {
  return typeof DecompressionStream !== 'undefined';
}

/** Parse a TIDY income-estimate PDF (bytes) into its annual figures. */
export async function parseIncomeEstimatePdf(bytes: Uint8Array): Promise<ParsedIncomeEstimate> {
  if (!isPdfImportSupported()) {
    throw new IncomeEstimateParseError('PDF import isn’t supported on this browser version (needs Safari/iOS 16.4+).');
  }
  const text = await extractPdfText(bytes);
  return extractIncomeEstimate(text);
}
